/**
 * Metadados, formatações e regras de tela do módulo de Eventos
 * (compartilhados por EventosList.vue e DetalhesEventos.vue).
 *
 * As regras de negócio (transições, efeito no equipamento, datas derivadas)
 * vivem no backend (Events/models.py); aqui só o que é apresentação e
 * validação prévia do formulário.
 */

// Mesma lista de backend/Events/api.py (GRUPOS_GESTAO)
export const GRUPOS_GESTAO = ['superadmin', 'engenhariaclinica', 'manager'];

export const podeGerirEventos = (grupos = []) => grupos.some((g) => GRUPOS_GESTAO.includes(g));

export const STATUS_META = {
  pendente: {
    label: 'Agendado',
    descricao: 'Aguardando a data de início',
    dot: 'bg-gray-400',
    badge: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    ring: 'ring-gray-400',
    icon: 'CalendarClock',
  },
  em_atendimento: {
    label: 'Em atendimento',
    descricao: 'Equipamento fora de operação',
    dot: 'bg-amber-400',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    ring: 'ring-amber-400',
    icon: 'Wrench',
  },
  finalizado: {
    label: 'Finalizado',
    descricao: 'Evento concluído',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    ring: 'ring-emerald-500',
    icon: 'CheckCircle2',
  },
};
const STATUS_DESCONHECIDO = {
  label: 'Indefinido',
  descricao: '',
  dot: 'bg-gray-300',
  badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  ring: 'ring-gray-300',
  icon: 'CircleHelp',
};
export const statusMeta = (status) => STATUS_META[status] || { ...STATUS_DESCONHECIDO, label: status || 'Indefinido' };

export const CATEGORIA_META = {
  preventiva: { label: 'Preventiva', longo: 'Manutenção preventiva', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  corretiva: { label: 'Corretiva', longo: 'Manutenção corretiva', badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300' },
  adverso: { label: 'Adverso', longo: 'Evento adverso', badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' },
};
export const categoriaMeta = (categoria) =>
  CATEGORIA_META[categoria] || { label: categoria || 'Outro', longo: categoria || 'Outro', badge: STATUS_DESCONHECIDO.badge };

export const EQUIPAMENTO_META = {
  em_funcionamento: { label: 'Em funcionamento', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  em_atendimento: { label: 'Em atendimento', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  parado: { label: 'Parado', badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
};
export const equipamentoMeta = (status) =>
  EQUIPAMENTO_META[status] || { label: status ? status.replace(/_/g, ' ') : 'Não informado', badge: STATUS_DESCONHECIDO.badge };

// Opções válidas ao finalizar um evento (o backend rejeita 'em_atendimento')
export const OPCOES_STATUS_APOS = [
  { valor: 'em_funcionamento', label: 'Em funcionamento', descricao: 'Equipamento liberado para uso' },
  { valor: 'parado', label: 'Parado', descricao: 'Equipamento continua fora de operação' },
];

// ---------------------------------------------------------------------------
// Datas / valores
// ---------------------------------------------------------------------------
export const hojeISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

/** Converte "AAAA-MM-DD" (data pura) sem deslocar o fuso. */
const dataLocal = (valor) => {
  if (!valor) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) {
    const [a, m, d] = valor.split('-').map(Number);
    return new Date(a, m - 1, d);
  }
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatarData = (valor, vazio = '—') => {
  const d = dataLocal(valor);
  return d ? d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : vazio;
};

export const formatarDataHora = (valor, vazio = '—') => {
  const d = dataLocal(valor);
  return d
    ? d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : vazio;
};

export const formatarMoeda = (valor) => {
  const n = Number(valor);
  if (!valor || Number.isNaN(n)) return 'R$ 0,00';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
};

/** Dias corridos entre a abertura e o fechamento (ou hoje, se ainda aberto). */
export const diasEmAberto = (evento) => {
  const inicio = dataLocal(evento.data_inicio) || dataLocal(evento.data_abertura);
  if (!inicio) return null;
  const fim = evento.status === 'finalizado' ? dataLocal(evento.data_fechamento) || dataLocal(evento.data_final) : new Date();
  if (!fim) return null;
  return Math.max(0, Math.round((fim.setHours(0, 0, 0, 0) - inicio.setHours(0, 0, 0, 0)) / 86400000));
};

export const tempoRelativo = (valor) => {
  const d = dataLocal(valor);
  if (!d) return '';
  const diff = Math.round((Date.now() - d.getTime()) / 60000);
  if (diff < 1) return 'agora';
  if (diff < 60) return `há ${diff} min`;
  const horas = Math.round(diff / 60);
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.round(horas / 24);
  if (dias < 30) return `há ${dias} d`;
  return formatarData(valor);
};

// ---------------------------------------------------------------------------
// Erros de API
// ---------------------------------------------------------------------------
export const mensagemErro = (error, fallback = 'Não foi possível concluir a operação.') => {
  const dados = error?.response?.data;
  if (!dados) return error?.message && !error?.response ? 'Sem conexão com o servidor.' : fallback;
  if (typeof dados.detail === 'string') return dados.detail;
  if (Array.isArray(dados.detail) && dados.detail.length) {
    const item = dados.detail[0];
    const campo = Array.isArray(item.loc) ? item.loc[item.loc.length - 1] : '';
    return campo ? `Campo "${campo}": ${item.msg}` : item.msg || fallback;
  }
  if (typeof dados.error === 'string') return dados.error;
  return fallback;
};

// ---------------------------------------------------------------------------
// Formulário de criação
// ---------------------------------------------------------------------------
export const formularioVazio = () => ({
  unidade: '',
  equipamento: '',
  entidade: '',
  categoria_evento: 'corretiva',
  status: 'em_atendimento',
  motivo_evento: '',
  solucao_evento: '',
  descricao: '',
  data_inicio: hojeISO(),
  data_final: '',
  sem_previsao: false,
  orcamento: null,
  is_parada: false,
  status_apos_evento: '',
});

/** Validação prévia (o backend repete tudo em Evento.clean()). Devolve {campo: mensagem}. */
export function validarFormulario(f) {
  const erros = {};
  if (!f.unidade) erros.unidade = 'Selecione a unidade.';
  if (!f.entidade) erros.entidade = 'Selecione a entidade.';
  if (!f.descricao || !f.descricao.trim()) erros.descricao = 'Descreva o evento.';
  if (!f.categoria_evento) erros.categoria_evento = 'Selecione a categoria.';

  const preventiva = f.categoria_evento === 'preventiva';
  if (preventiva && !f.data_inicio) erros.data_inicio = 'Preventiva exige data de início.';
  if (preventiva && !f.data_final && f.status !== 'finalizado') erros.data_final = 'Preventiva exige previsão de término.';
  if (f.status === 'pendente' && !f.data_inicio) erros.data_inicio = 'Agendamento exige a data de início.';
  if (f.status === 'pendente' && f.data_inicio && f.data_inicio <= hojeISO()) {
    erros.data_inicio = 'Para agendar, a data de início deve ser futura. Use "Em atendimento" se já começou.';
  }
  if (f.data_inicio && f.data_final && f.data_final < f.data_inicio) erros.data_final = 'A previsão não pode ser anterior ao início.';
  if (f.status === 'finalizado' && !f.status_apos_evento) erros.status_apos_evento = 'Informe como o equipamento ficou.';
  if (f.status === 'finalizado' && !f.data_final) erros.data_final = 'Informe a data de conclusão.';
  return erros;
}

/** Converte o estado do formulário no payload da API. */
export function montarPayload(f) {
  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  return {
    unidade: num(f.unidade),
    entidade: num(f.entidade),
    equipamento: num(f.equipamento),
    motivo_evento: num(f.motivo_evento),
    solucao_evento: num(f.solucao_evento),
    categoria_evento: f.categoria_evento,
    status: f.status,
    descricao: f.descricao.trim(),
    data_inicio: f.data_inicio || null,
    data_final: f.sem_previsao ? null : f.data_final || null,
    orcamento: f.orcamento === null || f.orcamento === '' ? null : Number(f.orcamento),
    is_parada: !!f.is_parada,
    status_apos_evento: f.status === 'finalizado' ? f.status_apos_evento || null : null,
  };
}
